import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProviderTokenService } from '../../services/provider-token.service';
import { ProviderTokenProjectRef, ProviderTokenSlot, ProviderType } from '../../models/provider-token.model';

interface RowState {
  slot: ProviderTokenSlot;
  expanded: boolean;
  draft: string;
  busy: boolean;
}

@Component({
  selector: 'app-provider-tokens-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TranslatePipe, MatIcon, MatProgressSpinner],
  templateUrl: './provider-tokens-section.component.html',
  styleUrl: './provider-tokens-section.component.scss',
})
export class ProviderTokensSectionComponent implements OnInit {
  private readonly service = inject(ProviderTokenService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly rows = signal<RowState[]>([]);

  readonly missingCount = computed(() => this.rows().filter((r) => !r.slot.tokenSet).length);
  readonly hasSlots = computed(() => this.rows().length > 0);

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (slots) => {
          this.rows.set(
            slots.map((slot) => ({
              slot,
              expanded: !slot.tokenSet,
              draft: '',
              busy: false,
            })),
          );
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  toggleRow(row: RowState): void {
    const key = rowKey(row);
    this.rows.update((rows) => rows.map((r) => (rowKey(r) === key ? { ...r, expanded: !r.expanded, draft: '' } : r)));
  }

  onDraftInput(row: RowState, value: string): void {
    const key = rowKey(row);
    this.rows.update((rows) => rows.map((r) => (rowKey(r) === key ? { ...r, draft: value } : r)));
  }

  save(row: RowState): void {
    const token = row.draft.trim();
    if (!token || row.busy) return;
    const key = rowKey(row);
    this.setBusy(key, true);
    this.service
      .upsert({ providerType: row.slot.providerType, host: row.slot.host, apiToken: token })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackBar.open(this.translate.instant('providerTokens.snack.saved'), 'OK', { duration: 3000 });
          this.reload();
        },
        error: () => {
          this.setBusy(key, false);
          this.snackBar.open(this.translate.instant('providerTokens.snack.error'), 'OK', { duration: 5000 });
        },
      });
  }

  remove(row: RowState): void {
    if (row.busy) return;
    const key = rowKey(row);
    this.setBusy(key, true);
    this.service
      .delete(row.slot.providerType, row.slot.host)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.snackBar.open(this.translate.instant('providerTokens.snack.deleted'), 'OK', { duration: 3000 });
          this.reload();
        },
        error: () => {
          this.setBusy(key, false);
          this.snackBar.open(this.translate.instant('providerTokens.snack.error'), 'OK', { duration: 5000 });
        },
      });
  }

  providerLabel(providerType: ProviderType): string {
    return providerType.toLowerCase();
  }

  joinProjectNames(projects: ProviderTokenProjectRef[]): string {
    const shown = projects.slice(0, 3).map((p) => p.name);
    const suffix = projects.length > 3 ? ' …' : '';
    return shown.join(' · ') + suffix;
  }

  providerHelpKey(providerType: ProviderType): string {
    return `providerTokens.help.${providerType.toLowerCase()}`;
  }

  actionLabelKey(row: RowState): string {
    if (row.expanded) return 'providerTokens.action.close';
    return row.slot.tokenSet ? 'providerTokens.action.rotate' : 'providerTokens.action.set';
  }

  private setBusy(key: string, busy: boolean): void {
    this.rows.update((rows) => rows.map((r) => (rowKey(r) === key ? { ...r, busy } : r)));
  }
}

function rowKey(row: RowState): string {
  return `${row.slot.providerType}|${row.slot.host}`;
}
